/**
 * useLaunchStudentActivity — le lanceur d'activité de l'espace élève, le même pour tous ses écrans
 * (la prochaine activité de l'accueil, une ligne de l'onglet Activités, la mission de Sam).
 *
 * L'exécution reste sur l'ancien code, poussée en plein écran : ce crochet ne fait que choisir la
 * PORTE. La règle est celle de l'ancien accueil (`TodoListScreen.js`, et la bannière
 * `NextLiveSessionBanner.js`) : hors live, un élève pour qui Sam est ouvert (`isSamOpenFor` : le
 * coupe-circuit de Sam et `user.sam_eligible`) part dans `sam-session` ; sinon dans `activity`,
 * après avoir posé l'activité courante comme le fait `useLaunchActivity` (`common/hooks`).
 *
 * L'espace élève n'existe que hors live : la branche « en live » du legacy n'a pas lieu d'être ici.
 */
import { useNavigation } from "@react-navigation/native"
import { useCallback } from "react"
import { useDispatch, useSelector } from "react-redux"

import { isSamOpenFor } from "../../../../data/constants/samConstants"
import { setCurrentActivity } from "../../../../data/redux/actions/activity"
import { selectDisplayedStudent } from "../../view-models/shared/displayedStudent"

/** L'activité telle que le backend la sert : l'écran d'exécution legacy la relit en entier. */
export type LaunchableActivity = { id: string | number } & Record<string, unknown>

export type StudentActivityLaunch =
    | { route: "sam-session"; params: { activityId: string | number; entry_point?: string } }
    | { route: "activity"; params: undefined }

/** La porte, pure : `sam-session` si Sam est ouvert pour cet élève, `activity` sinon. */
export function studentActivityLaunch(
    activityId: string | number,
    samOpen: boolean,
    entryPoint?: string
): StudentActivityLaunch {
    if (samOpen) {
        return {
            route: "sam-session",
            params: entryPoint ? { activityId, entry_point: entryPoint } : { activityId },
        }
    }
    return { route: "activity", params: undefined }
}

type PushNavigation = { push: (route: string, params?: object) => void }

/**
 * Rend `launch(activity, entryPoint?)`. `entryPoint` est relayé à Sam pour ses mesures
 * (`"banner"` pour la mission de l'accueil, comme la bannière legacy).
 */
export function useLaunchStudentActivity() {
    const navigation = useNavigation() as unknown as PushNavigation
    const dispatch = useDispatch()
    const samOpen = useSelector(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state: any) => isSamOpenFor(selectDisplayedStudent(state)) as boolean
    )

    return useCallback(
        (activity: LaunchableActivity, entryPoint?: string) => {
            const launch = studentActivityLaunch(activity.id, samOpen, entryPoint)
            if (launch.route === "activity") {
                dispatch(setCurrentActivity({ activityData: activity, progressStatus: "ongoing" }) as never)
            }
            navigation.push(launch.route, launch.params)
        },
        [dispatch, navigation, samOpen]
    )
}
